import { useEffect, useState } from 'react';
import { SHARED_BOARDS } from '../../shared/doc.js';
import { api, type Me, type Project } from '../api.js';
import { Canvas } from '../canvas/Canvas.js';
import { Avatars, usePeers } from '../canvas/Presence.js';
import { CardPanel } from '../cards/CardPanel.js';
import { problemBanner, useToasts } from '../components.js';
import { useProject } from '../doc/useProject.js';
import { HistoryPanel } from '../history/HistoryPanel.js';
import { MapView } from '../map/MapView.js';
import { PhoneProject } from '../phone/PhoneProject.js';
import { href, usePhone, type ProjectView } from '../router.js';
import { SparkList } from '../spark/SparkList.js';
import { Tray } from '../tray/Tray.js';
import { Members } from './Members.js';

const TABS: { view: ProjectView; label: string }[] = [
  { view: 'spark', label: 'Spark' },
  { view: 'research', label: 'Research' },
  { view: 'moodboard', label: 'Moodboard' },
  { view: 'map', label: 'Map' },
  { view: 'history', label: 'History' },
  { view: 'members', label: 'Members' },
];

export function ProjectPage({ me, projectId, view, focus = null }: { me: Me; projectId: string; view: ProjectView; focus?: string | null }) {
  const [project, setProject] = useState<Project | null>(null);
  const [error, setError] = useState('');
  const [openCard, setOpenCard] = useState<string | null>(null);
  const conn = useProject(projectId, me);
  const peers = usePeers(conn);
  const phone = usePhone();
  const { toast, view: toasts } = useToasts();

  useEffect(() => {
    api<Project>('GET', `/api/projects/${projectId}`).then(setProject, (e) => setError(e.message));
    localStorage.setItem('plana-last-project', projectId);
  }, [projectId]);

  if (error) return <main className="error-page"><p role="alert">{error}</p><a href={href.projects()}>All projects</a></main>;
  if (!project || !conn) return <p className="loading">Loading…</p>;

  const banner =
    problemBanner(conn.problem) ??
    (conn.status !== 'connected' && conn.synced ? (
      <p className="banner subtle" role="status">Offline: your changes are kept and will sync when the connection is back.</p>
    ) : null);

  if (phone) return <PhoneProject conn={conn} project={project} banner={banner} view={view} focus={focus} />;

  const boardId = view === 'moodboard' ? SHARED_BOARDS.moodboard : SHARED_BOARDS.research;
  return (
    <main className="project">
      <header className="topbar">
        <a href={href.projects()} className="back">← Projects</a>
        <h1>{project.title}</h1>
        {project.timeWindow && <span className="muted">{project.timeWindow}</span>}
        <span className="spacer" />
        <Avatars peers={peers} me={{ name: me.name, colour: conn.colour }} />
      </header>
      <nav className="tabs">
        {TABS.map((t) => (
          <a key={t.view} href={href.project(projectId, t.view)} aria-current={t.view === view ? 'page' : undefined}>
            {t.label}
          </a>
        ))}
      </nav>
      {banner}
      {!conn.synced ? (
        <p className="loading">Connecting…</p>
      ) : view === 'spark' ? (
        <SparkList conn={conn} projectId={projectId} onOpenCard={setOpenCard} toast={toast} />
      ) : view === 'history' ? (
        <HistoryPanel projectId={projectId} role={project.role} />
      ) : view === 'members' ? (
        <Members project={project} me={me} onChange={setProject} />
      ) : view === 'map' ? (
        <MapView conn={conn} projectId={projectId} onOpenCard={setOpenCard} toast={toast} focusPlaceId={focus} />
      ) : (
        <div className="board-screen">
          <SparkList conn={conn} projectId={projectId} onOpenCard={setOpenCard} toast={toast} compact />
          <Canvas key={boardId} conn={conn} projectId={projectId} boardId={boardId} peers={peers} onOpenCard={setOpenCard} toast={toast} />
          {view === 'research' && <Tray conn={conn} onOpenCard={setOpenCard} />}
        </div>
      )}
      {openCard && <CardPanel conn={conn} projectId={projectId} cardId={openCard} onOpenCard={setOpenCard} onClose={() => setOpenCard(null)} />}
      {toasts}
    </main>
  );
}
